import type {
  AdminMeeting,
  AdminStats,
  AdminUser,
  Moderator,
  PhotoToValidate,
  QuestionToModerate,
  RegistrationStats,
} from '@/entities/admin';
import { apiClient } from '@/shared/api/api-client';
import type { ApiResponse } from '@/shared/api/types';

export const adminApi = {
  // Get admin dashboard stats
  getStats: async (): Promise<AdminStats> => {
    // No fallback: a failure must show an error, not fake zeros
    const response = await apiClient.get<ApiResponse<AdminStats>>('/v1/admin/stats');
    return response.data.data;
  },

  // Search users
  searchUsers: async (params: {
    query?: string;
    type?: number;
    genderId?: number;
    page?: number;
    limit?: number;
    /** column key whitelisted by the API (nodeId, username, email, createdAt, lastActivity, isPhotoVerified, nodeType) */
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<{ users: AdminUser[]; total: number }> => {
    const response = await apiClient.get<ApiResponse<{ users: AdminUser[]; total: number }>>(
      '/v1/admin/users',
      { params },
    );
    return response.data.data;
  },

  // Get user details
  getUser: async (nodeId: number): Promise<AdminUser | null> => {
    try {
      const response = await apiClient.get<ApiResponse<AdminUser>>(`/v1/admin/users/${nodeId}`);
      return response.data.data;
    } catch {
      return null;
    }
  },

  // Change user status
  changeStatus: async (nodeId: number, statusId: number): Promise<void> => {
    await apiClient.put(`/v1/admin/users/${nodeId}/status`, { statusId });
  },

  // Change user role
  changeRole: async (nodeId: number, roleId: number): Promise<void> => {
    await apiClient.put(`/v1/admin/users/${nodeId}/role`, { roleId });
  },

  // Verify photo
  verifyPhoto: async (nodeId: number, verified: boolean): Promise<void> => {
    await apiClient.put(`/v1/admin/users/${nodeId}/photo`, { verified });
  },

  // Add note/rating
  addNote: async (nodeId: number, note: number): Promise<void> => {
    await apiClient.put(`/v1/admin/users/${nodeId}/note`, { note });
  },

  // Change gender
  changeGender: async (nodeId: number, genderId: number): Promise<void> => {
    await apiClient.put(`/v1/admin/users/${nodeId}/gender`, { genderId });
  },

  // Add admin comment
  addComment: async (nodeId: number, comment: string): Promise<void> => {
    await apiClient.post(`/v1/admin/users/${nodeId}/comment`, { comment });
  },

  // Get photos to validate
  getPhotosToValidate: async (
    page = 1,
    limit = 20,
  ): Promise<{ photos: PhotoToValidate[]; total: number }> => {
    try {
      const response = await apiClient.get<
        ApiResponse<{ photos: PhotoToValidate[]; total: number }>
      >('/v1/admin/photos', { params: { page, limit } });
      return response.data.data;
    } catch {
      return { photos: [], total: 0 };
    }
  },

  // Get meetings
  getMeetings: async (status?: 'pending' | 'assigned' | 'archived'): Promise<AdminMeeting[]> => {
    // No fallback: an API failure must show an error, not "no meeting"
    const response = await apiClient.get<ApiResponse<AdminMeeting[]>>('/v1/admin/meetings', {
      params: { status },
    });
    return response.data.data ?? [];
  },

  getModerators: async (): Promise<Moderator[]> => {
    const response = await apiClient.get<ApiResponse<Moderator[]>>('/v1/admin/moderators');
    return response.data.data ?? [];
  },

  assignModerator: async (interactionId: number, moderatorId: number): Promise<void> => {
    await apiClient.post(`/v1/admin/meetings/${interactionId}/assign`, { moderatorId });
  },

  // Archive meeting
  archiveMeeting: async (interactionId: number): Promise<void> => {
    await apiClient.post(`/v1/admin/meetings/${interactionId}/archive`);
  },

  // Delete meeting
  deleteMeeting: async (interactionId: number): Promise<void> => {
    await apiClient.delete(`/v1/admin/meetings/${interactionId}`);
  },

  // Validate meeting
  validateMeeting: async (interactionId: number): Promise<void> => {
    await apiClient.post(`/v1/admin/meetings/${interactionId}/validate`);
  },

  // Get questions to moderate
  getQuestionsToModerate: async (
    page = 1,
    limit = 20,
  ): Promise<{ questions: QuestionToModerate[]; total: number }> => {
    try {
      const response = await apiClient.get<
        ApiResponse<{ questions: QuestionToModerate[]; total: number }>
      >('/v1/admin/questions', { params: { page, limit } });
      return response.data.data;
    } catch {
      return { questions: [], total: 0 };
    }
  },

  // Validate question
  validateQuestion: async (questionId: number): Promise<void> => {
    await apiClient.post(`/v1/admin/questions/${questionId}/validate`);
  },

  // Delete question
  deleteQuestion: async (questionId: number): Promise<void> => {
    await apiClient.delete(`/v1/admin/questions/${questionId}`);
  },

  // Get registration stats
  getRegistrationStats: async (): Promise<RegistrationStats> => {
    const response = await apiClient.get<ApiResponse<RegistrationStats>>(
      '/v1/admin/stats/registrations',
    );
    return response.data.data;
  },
};
